import { INIT_PAGINATION, Share_Type } from '@vindr/services/constants/common.constant';
import { SORT_ORDER } from '@vindr/services/constants/filter.constant';
import { PERMISSIONS } from '@vindr/services/constants/permissions.constant';
import useControlModal from '@vindr/utils/hooks/useControlModal';
import { PaginationProps, SettingsContext } from '@vindr/services/interfaces';
import { useGetShares } from '@vindr/services/actions/share.action';
import keycloak from '@vindr/services/keycloak';
import { filterValidValue } from '@vindr/utils/helpers/common.helper';
import dayjs from 'dayjs';
import { Dispatch, createContext, useContext, useMemo, useState } from 'react';
import { ImmerHook, useImmer } from 'use-immer';
import { ACCESS_TYPE } from '@vindr/services/apis/share.api';

export const INIT_FILTER_SHARE: Parameters<typeof useGetShares>[0] = {
  sort_field: 'created_at',
  sort_order: SORT_ORDER.DESC,
  ...(!keycloak.hasOneOfPerm([PERMISSIONS.page.admin_settings]) && {
    creator_ids: [keycloak.getUserInfo()?.id],
  }),
  type: Share_Type.Guest,
  created_at_from: dayjs().startOf('days').toISOString(),
  created_at_to: dayjs().endOf('days').toISOString(),
  access_type: ACCESS_TYPE.Study,
};

export interface ShareContext extends SettingsContext {
  userShareList: ReturnType<typeof useGetShares>['data']['data']['data'];
  refetch: ReturnType<typeof useGetShares>['refetch'];
  editItem: ShareContext['userShareList'][number];
  filter: Parameters<typeof useGetShares>[0];
  setFilter: ImmerHook<ShareContext['filter']>[1];
  setStateAction: Dispatch<React.SetStateAction<'create' | 'edit'>>;
}

const ShareContext = createContext({});
const ShareProvider = ({ children }: { children: React.ReactNode }) => {
  const [editItem, setEditItem] = useState<ShareContext['editItem']>(null);
  const [stateAction, setStateAction] = useState<'create' | 'edit'>('create');
  const [filter, setFilter] = useImmer<ShareContext['filter']>(INIT_FILTER_SHARE);
  const [pagination, setPagination] = useImmer<PaginationProps>(INIT_PAGINATION);

  const { openModal, setCloseModal, setOpenAddModal, setOpenEditModal } = useControlModal(
    setEditItem,
    setStateAction
  );

  const { data, isFetching, refetch } = useGetShares({
    limit: pagination.size,
    offset: (pagination.page - 1) * pagination.size,
    ...filterValidValue(filter),
  });

  const { count: total } = data || {};
  const { data: userShareList } = data?.data || {};

  const memoizedValue = useMemo(
    () => ({
      editItem,
      filter,
      isLoading: isFetching,
      refetch,
      openModal,
      pagination,
      setCloseModal,
      setEditItem,
      setFilter,
      setOpenAddModal,
      setOpenEditModal,
      setPagination,
      stateAction,
      setStateAction,
      total,
      userShareList,
    }),
    [
      editItem,
      filter,
      isFetching,
      refetch,
      openModal,
      pagination,
      setCloseModal,
      setEditItem,
      setFilter,
      setOpenAddModal,
      setOpenEditModal,
      setPagination,
      stateAction,
      setStateAction,
      total,
      userShareList,
    ]
  );

  return <ShareContext.Provider value={memoizedValue}>{children}</ShareContext.Provider>;
};

ShareProvider.displayName = 'ShareProvider';

export const useShareContext = () => {
  const context = useContext(ShareContext);

  if (!context) throw new Error('useShareContext must be used within a ShareProvider');

  return context as ShareContext;
};

export default ShareProvider;
